import { faUsersGear, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useAppVersion } from '../../../foundation/config/appVersion';
import {
  BRAND_PRIMARY,
  CONFIRM_YELLOW,
  WARNING_BG,
  WARNING_BORDER,
} from '../../../foundation/config/colors';
import {
  useGetVariantDetailQuery,
  useGetVariantHistoryQuery,
} from '../../api/variantApi';
import { selectUser } from '../../authentication/redux/selectors';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { VariantTranscript } from '../redux';
import RefreshVariantDataButton from '../summary/RefreshVariantDataButton';
import TranscriptListModal from './TranscriptListModal';
import VariantUpdatePrimaryTranscriptModal from './VariantUpdatePrimaryTranscriptModal';

const SUPPORT_EMAIL = 'dnawiz-support@biologis.eu';

const VariantUpdatePrimaryTranscript = () => {
  const params = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const lock = useSelector(selectVariantLock);
  const user = useSelector(selectUser);
  const { isReadOnly, isUserInstituteAllowed } = useVariantAccess();
  const appVersion = useAppVersion();
  // Read history straight from the query hook's cache rather than a selector
  const { data: variantHistory } = useGetVariantHistoryQuery(
    { variant_id: variant?.id },
    { skip: !variant?.id },
  );

  const [isVariantModalVisible, setVariantModalVisibility] = useState(false);
  const [isTranscriptModalVisible, setTranscriptModalVisibility] =
    useState(false);

  const [selectedPrimaryVariantTranscriptData, setNewPrimaryTranscript] =
    useState<VariantTranscript | null>(null);

  // Generate the variant link string for display in header
  const variant_link = useMemo(() => {
    return `${variant?.transcript_external_id}(${variant?.gene_symbol}):${variant?.cdna}(${variant?.protein_change})`;
  }, [variant]);

  const showInstituteWarning =
    !isUserInstituteAllowed &&
    Array.isArray(variant?.institutes) &&
    variant.institutes.length > 0;

  const instituteAccessMailto = useMemo(() => {
    const subject = 'DNAwiz - Institute Access Request';
    const body = [
      'Hello DNAwiz Support,',
      '',
      'I would like to request editing access to the following variant:',
      '',
      `Variant: ${variant_link}`,
      `Variant ID: ${variant?.variant_id}`,
      '',
      `User: ${user?.email || user?.username || 'Unknown'}`,
      `Page: ${window.location.href}`,
      `Version: ${appVersion}`,
    ].join('\n');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [variant_link, variant?.variant_id, user, appVersion]);

  const handleVariantLinkModalClose = () => {
    setVariantModalVisibility(false);
  };

  const handleTranscriptModalClose = () => {
    setTranscriptModalVisibility(false);
  };

  const handleSelectTranscriptClick = () => {
    setTranscriptModalVisibility(true);
  };

  const handleChangePrimaryTranscript = (record: VariantTranscript) => {
    setNewPrimaryTranscript(record);
    handleTranscriptModalClose();
  };

  return (
    <div className="variant-summary__link">
      {isVariantModalVisible && (
        <VariantUpdatePrimaryTranscriptModal
          closeModal={handleVariantLinkModalClose}
          variantLink={variant_link}
          handleSelectTranscriptClick={handleSelectTranscriptClick}
          selectedPrimaryVariantTranscriptData={
            selectedPrimaryVariantTranscriptData
          }
          setNewPrimaryTranscript={setNewPrimaryTranscript}
        />
      )}

      {isTranscriptModalVisible && (
        <TranscriptListModal
          closeModal={handleTranscriptModalClose}
          onChangePrimaryTranscript={handleChangePrimaryTranscript}
        />
      )}
      <a
        onClick={() => setVariantModalVisibility(true)}
        style={{ textDecoration: 'underline' }}
      >{`Variant: ${variant_link}`}</a>
      {isReadOnly && (
        <Tooltip
          title={`Variant currently being edited by ${lock?.locked_by}`}
          placement="right"
        >
          <FontAwesomeIcon
            icon={faUsersGear}
            className="variant-summary__lock-indicator"
            style={{ color: CONFIRM_YELLOW, fontSize: 18, lineHeight: 1 }}
          />
        </Tooltip>
      )}

      {showInstituteWarning && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: WARNING_BG,
            border: `1px solid ${WARNING_BORDER}`,
            borderRadius: 6,
            padding: '4px 10px',
          }}
        >
          <FontAwesomeIcon
            icon={faWarning}
            style={{
              color: CONFIRM_YELLOW,
              fontSize: 18,
              lineHeight: 1,
              flexShrink: 0,
            }}
          />
          <div style={{ fontWeight: 'normal' }}>
            <div style={{ fontSize: '13px', color: BRAND_PRIMARY }}>
              This variant is currently maintained by{' '}
              <strong>{variantHistory?.institution}</strong>.
            </div>
            <div style={{ fontSize: '13px', color: BRAND_PRIMARY }}>
              <a
                href={instituteAccessMailto}
                style={{ textDecoration: 'underline' }}
              >
                Contact support
              </a>{' '}
              to request editing rights.
            </div>
          </div>
        </div>
      )}
      <RefreshVariantDataButton
        lastUpdated={
          variantHistory?.changelog[0]?.changed ||
          (variantHistory?.interpreted !== '-'
            ? variantHistory?.interpreted
            : undefined)
        }
      />
    </div>
  );
};

export default VariantUpdatePrimaryTranscript;
